// src/services/task.service.js
import { api } from './api';
import taskSyncService from './taskSync.service';

class TaskService {
    // ============================================================
    // TASKS
    // ============================================================

    /**
     * Get all tasks with optional filters
     */
    async getTasks(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.type) params.append('type', filters.type);
            if (filters.status) params.append('status', filters.status);
            if (filters.priority) params.append('priority', filters.priority);
            if (filters.category) params.append('category', filters.category);

            const response = await api.get(`/api/tasks?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching tasks:', error);
            throw error;
        }
    }

    /**
     * Get single task by ID
     */
    async getTask(taskId) {
        try {
            const response = await api.get(`/api/tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching task:', error);
            throw error;
        }
    }

    /**
     * Create new task
     */
    async createTask(taskData) {
        try {
            const response = await api.post('/api/tasks', taskData);
            return response.data;
        } catch (error) {
            console.error('Error creating task:', error);
            throw error;
        }
    }

    /**
     * Update task
     */
    async updateTask(taskId, taskData) {
        try {
            const response = await api.put(`/api/tasks/${taskId}`, taskData);

            // If this is a personal task linked to a team task, trigger sync
            if (response.success && taskData.team_task_id) {
                console.log(`🔄 Task is linked to team task ${taskData.team_task_id}, checking sync requirements`);
                taskSyncService.syncTeamTaskFromPersonalTask(taskId, taskData.status).catch(syncError => {
                    console.error('Error syncing team task from personal task update:', syncError);
                });
            }

            return response.data;
        } catch (error) {
            console.error('Error updating task:', error);
            throw error;
        }
    }

    /**
     * Delete task
     */
    async deleteTask(taskId) {
        try {
            const response = await api.delete(`/api/tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting task:', error);
            throw error;
        }
    }

    /**
     * Mark task as complete
     */
    async completeTask(taskId) {
        try {
            const response = await api.put(`/api/tasks/${taskId}/complete`);
            return response.data;
        } catch (error) {
            console.error('Error completing task:', error);
            throw error;
        }
    }

    /**
     * Mark task as incomplete
     */
    async uncompleteTask(taskId) {
        try {
            const response = await api.put(`/api/tasks/${taskId}/uncomplete`);
            return response.data;
        } catch (error) {
            console.error('Error uncompleting task:', error);
            throw error;
        }
    }

    /**
     * Update task priority
     */
    async updatePriority(taskId, priority) {
        try {
            const response = await api.put(`/api/tasks/${taskId}/priority`, { priority });
            return response.data;
        } catch (error) {
            console.error('Error updating priority:', error);
            throw error;
        }
    }

    /**
     * Reorder tasks
     */
    async reorderTasks(taskOrders) {
        try {
            const response = await api.put('/api/tasks/reorder', { taskOrders });
            return response.data;
        } catch (error) {
            console.error('Error reordering tasks:', error);
            throw error;
        }
    }

    /**
     * Cleanup completed daily tasks (EOD)
     */
    async cleanupCompletedDailyTasks() {
        try {
            const response = await api.post('/api/tasks/cleanup-daily');
            return response.data;
        } catch (error) {
            console.error('Error cleaning up daily tasks:', error);
            throw error;
        }
    }

    /**
     * Check if EOD cleanup is needed and perform it
     * Should be called once per day (checks localStorage for last cleanup date)
     */
    async checkAndRunEODCleanup() {
        try {
            const lastCleanup = localStorage.getItem('lastTaskCleanup');
            const today = new Date().toDateString();

            // Only run cleanup once per day
            if (lastCleanup !== today) {
                //console.log('🧹 Running EOD cleanup for completed daily tasks...');
                const result = await this.cleanupCompletedDailyTasks();
                localStorage.setItem('lastTaskCleanup', today);
                //console.log(`✅ Cleaned up ${result.deletedCount} completed daily tasks`);
                return result;
            } else {
                //console.log('✓ EOD cleanup already ran today');
                return { success: true, alreadyRan: true };
            }
        } catch (error) {
            console.error('Error in EOD cleanup check:', error);
            // Don't throw - cleanup failure shouldn't break the app
            return { success: false, error: error.message };
        }
    }

    // ============================================================
    // ACTIVITIES & UPCOMING TASKS
    // ============================================================

    /**
     * Get recent activities (real-time task tracking)
     */
    async getRecentActivities(limit = 10) {
        try {
            const response = await api.get(`/api/tasks/activities/recent?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching recent activities:', error);
            throw error;
        }
    }

    /**
     * Get upcoming tasks sorted by deadline
     */
    async getUpcomingTasks(limit = 5) {
        try {
            const response = await api.get(`/api/tasks/upcoming?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching upcoming tasks:', error);
            throw error;
        }
    }

    // ============================================================
    // CHECKLIST
    // ============================================================

    /**
     * Add checklist item to task
     */
    async addChecklistItem(taskId, itemData) {
        try {
            const response = await api.post(`/api/tasks/${taskId}/checklist`, itemData);
            return response.data;
        } catch (error) {
            console.error('Error adding checklist item:', error);
            throw error;
        }
    }

    /**
     * Update checklist item
     */
    async updateChecklistItem(taskId, itemId, itemData) {
        try {
            const response = await api.put(`/api/tasks/${taskId}/checklist/${itemId}`, itemData);
            return response.data;
        } catch (error) {
            console.error('Error updating checklist item:', error);
            throw error;
        }
    }

    /**
     * Delete checklist item
     */
    async deleteChecklistItem(taskId, itemId) {
        try {
            const response = await api.delete(`/api/tasks/${taskId}/checklist/${itemId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting checklist item:', error);
            throw error;
        }
    }
}

export default new TaskService();
